import React from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

export const eur = (v) => (v == null ? '—' : `€${Number(v).toLocaleString('en-IE', { maximumFractionDigits: 0 })}`);
export const num = (v, d = 0) => (v == null ? '—' : Number(v).toLocaleString('en-IE', { maximumFractionDigits: d }));
export const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const PALETTE = ['#0f766e', '#f59e0b', '#2563eb', '#dc2626', '#7c3aed', '#059669', '#ea580c', '#0891b2', '#be123c', '#4d7c0f', '#6d28d9', '#b45309'];

export const Delta = ({ v, invert = false, testId }) => {
  if (v == null) return <span className="text-[11px] text-slate-400" data-testid={testId}>—</span>;
  const good = invert ? v < 0 : v > 0;
  return <span className={`text-[11px] font-semibold ${good ? 'text-emerald-700' : 'text-rose-700'}`} data-testid={testId}>{v > 0 ? '▲' : '▼'} {Math.abs(v).toFixed(1)}% vs LY</span>;
};

export const Kpi = ({ label, value, delta, invert, hint, testId }) => (
  <div className="rounded-xl border bg-white p-3 shadow-sm" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-xl font-bold text-slate-900 mt-0.5">{value}</p>
    <div className="flex items-center justify-between mt-0.5"><Delta v={delta} invert={invert} />{hint && <span className="text-[10px] text-slate-400">{hint}</span>}</div>
  </div>
);

export const Card = ({ title, sub, children, testId, right }) => (
  <div className="rounded-xl border bg-white p-4 shadow-sm" data-testid={testId}>
    <div className="flex items-start justify-between gap-2 mb-2">
      <div><h3 className="text-sm font-bold text-slate-900">{title}</h3>{sub && <p className="text-[11px] text-slate-500">{sub}</p>}</div>
      {right}
    </div>
    {children}
  </div>
);

const tip = (fmt) => ({ formatter: (v, name) => [fmt ? fmt(v) : v, name], contentStyle: { fontSize: 12 } });

export const MonthlyOccupancy = ({ cur, prev, year }) => {
  const prevMap = {};
  (prev || []).forEach((m) => { prevMap[m.m] = m; });
  const data = (cur || []).map((m) => ({ month: m.month, occ: m.occupancy, occPrev: prevMap[m.m]?.occupancy ?? null, adr: m.adr, adrPrev: prevMap[m.m]?.adr ?? null }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
        <YAxis yAxisId="l" tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
        <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} unit="€" />
        <Tooltip {...tip()} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line yAxisId="l" type="monotone" dataKey="occ" name={`Occupancy ${year}`} stroke="#0f766e" strokeWidth={2.5} dot={false} />
        <Line yAxisId="l" type="monotone" dataKey="occPrev" name={`Occupancy ${year - 1}`} stroke="#0f766e" strokeDasharray="4 4" dot={false} />
        <Line yAxisId="r" type="monotone" dataKey="adr" name={`ADR ${year}`} stroke="#f59e0b" strokeWidth={2.5} dot={false} />
        <Line yAxisId="r" type="monotone" dataKey="adrPrev" name={`ADR ${year - 1}`} stroke="#f59e0b" strokeDasharray="4 4" dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
};

export const MonthlyRevenue = ({ cur, prev, year }) => {
  const prevMap = {};
  (prev || []).forEach((m) => { prevMap[m.m] = m; });
  const data = (cur || []).map((m) => ({ month: m.month, revenue: m.revenue, prev: prevMap[m.m]?.revenue ?? 0, nights: m.nights, created: m.created, cancelled: m.cancelled }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${Math.round(v / 1000)}k`} />
        <Tooltip {...tip(eur)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="prev" name={`Revenue ${year - 1}`} fill="#cbd5e1" radius={[4, 4, 0, 0]} />
        <Bar dataKey="revenue" name={`Revenue ${year}`} fill="#0f766e" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const PaceChart = ({ cur }) => {
  const data = (cur || []).map((m) => ({ month: m.month, created: m.created, arrivals: m.arrivals, cancelled: m.cancelled }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip {...tip()} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Area type="monotone" dataKey="created" name="Bookings made" stroke="#2563eb" fill="#dbeafe" />
        <Area type="monotone" dataKey="arrivals" name="Arrivals" stroke="#0f766e" fill="#ccfbf1" />
        <Area type="monotone" dataKey="cancelled" name="Cancelled (by arrival month)" stroke="#dc2626" fill="#fee2e2" />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export const Histogram = ({ rows, color = '#0f766e', height = 180 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={rows || []}>
      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
      <YAxis tick={{ fontSize: 10 }} />
      <Tooltip {...tip()} />
      <Bar dataKey="count" name="Bookings" fill={color} radius={[4, 4, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

export const SharePie = ({ rows, dataKey = 'revenue', nameKey = 'label', fmt = eur }) => {
  const data = (rows || []).filter((r) => r[dataKey] > 0).slice(0, 8);
  const cells = data.map((r, i) => <Cell key={r[nameKey]} fill={PALETTE[i % PALETTE.length]} />);
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={data} dataKey={dataKey} nameKey={nameKey} innerRadius={50} outerRadius={85} paddingAngle={2}>{cells}</Pie>
        <Tooltip {...tip(fmt)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

export const BreakdownTable = ({ rows, cols, testId }) => {
  const list = rows || [];
  const heads = cols.map((c) => <th key={c.key} className={`pb-1 pr-2 ${c.right ? 'text-right' : 'text-left'}`}>{c.label}</th>);
  const body = list.map((r) => {
    const tds = cols.map((c) => <td key={c.key} className={`py-1 pr-2 ${c.right ? 'text-right tabular-nums' : ''} ${c.bold ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{c.fmt ? c.fmt(r[c.key], r) : r[c.key]}</td>);
    return <tr key={r.key || r.label || r.month || r.year || r.guest} className="border-t">{tds}</tr>;
  });
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-xs"><thead><tr className="text-[10px] uppercase text-slate-500">{heads}</tr></thead><tbody>{body}</tbody></table>
      {!list.length && <p className="text-xs text-slate-400 py-2">No data.</p>}
    </div>
  );
};
